import {
  Anchor,
  Box,
  Card,
  Center,
  Group,
  LoadingOverlay,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconChartBarOff } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { dateToEndOfDay, dateToStartOfDay } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';
import { CHANNELS, columnKey, formatStatValue } from './ChannelBreakdownTable';
import FlowStepStatsGraph from './FlowStepStatsGraph';

const channelByScope = new Map(CHANNELS.map((channel) => [channel.scope, channel]));

const StepBreakdown = ({ campaign, startDate, endDate }) => {
  const { accountId, domainId } = useParams();
  const fetch = useFetch();
  const currency = useSelector((state) => state.domain.domain?.currency);
  const [view, setView] = useState('table');
  const [response, setResponse] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    if (!startDate || !endDate || !campaign?.id) return;
    const startTs = dateToStartOfDay(startDate, 'local');
    const endTs = dateToEndOfDay(endDate, 'local');
    setResponse(undefined);
    setError(undefined);
    let cancelled = false;
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaign.id}/flowStepStats/${encodeURIComponent(
        startTs
      )}/${encodeURIComponent(endTs)}`
    )
      .then((data) => {
        if (!cancelled) setResponse(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, domainId, fetch, startDate, endDate, campaign?.id]);

  // Unlike the by-channel table, a message reused in two flow steps shows as
  // two rows — that distinction is the point of flowStepId.
  const steps = useMemo(() => {
    const nodeById = new Map((campaign?.flow?.nodes || []).map((node) => [String(node.id), node]));
    return (response?.steps || []).map((step, index) => ({
      ...step,
      index: index + 1,
      node: nodeById.get(String(step.flowStepId)),
      config: channelByScope.get(step.channel),
    }));
  }, [response, campaign?.flow?.nodes]);

  const stepsById = useMemo(() => new Map(steps.map((step) => [String(step.flowStepId), step])), [steps]);

  const { unifiedColumns, columnLookupByScope } = useMemo(() => {
    const seenColumnKeys = new Set();
    const columns = [];
    const lookup = new Map();
    for (const step of steps) {
      if (!step.config || lookup.has(step.channel)) continue;
      lookup.set(step.channel, new Map(step.config.columns.map((col) => [columnKey(col), col])));
      for (const col of step.config.columns) {
        const key = columnKey(col);
        if (seenColumnKeys.has(key)) continue;
        seenColumnKeys.add(key);
        columns.push({ ...col, key });
      }
    }
    return { unifiedColumns: columns, columnLookupByScope: lookup };
  }, [steps]);

  const isLoading = !response && !error;

  // A whole-request failure (e.g. missing endpoint, 404) leaves `response`
  // undefined, so no rows would render. Surface it as a top-level state that
  // covers both the table and flow views.
  if (error) {
    return (
      <Card padding="md">
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconChartBarOff size={48} color="var(--mantine-color-red-6)" stroke={1.5} />
            <Title c="dimmed" order={4} ta="center">
              Couldn’t load per-step statistics
            </Title>
            <Text c="dimmed" size="sm" ta="center">
              {error?.message || 'Something went wrong while loading step statistics.'}
            </Text>
          </Stack>
        </Center>
      </Card>
    );
  }

  if (response && steps.length === 0) {
    return (
      <Card padding="md">
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconChartBarOff size={48} color="var(--mantine-color-dimmed)" stroke={1.5} />
            <Title c="dimmed" order={4} ta="center">
              No message steps found in this flow
            </Title>
            <Text c="dimmed" size="sm" ta="center">
              Add an Email, SMS, WhatsApp, Viber, Push, Webhook, Facebook Ad or Google Ad step to see per-step
              statistics.
            </Text>
          </Stack>
        </Center>
      </Card>
    );
  }

  return (
    <Card padding="md" withBorder>
      <Group justify="flex-end" mb="sm">
        <SegmentedControl
          size="xs"
          value={view}
          onChange={setView}
          data={[
            { value: 'table', label: 'Table' },
            { value: 'flow', label: 'Flow' },
          ]}
        />
      </Group>
      <Box pos="relative">
        <LoadingOverlay visible={isLoading} zIndex={2} overlayProps={{ blur: 1, backgroundOpacity: 0.4 }} />
        {view === 'flow' ? (
          <FlowStepStatsGraph campaign={campaign} stepsById={stepsById} currency={currency} />
        ) : (
          <Table striped withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '1%', whiteSpace: 'nowrap' }}>Step</Table.Th>
                <Table.Th style={{ width: '1%', whiteSpace: 'nowrap' }}>Channel</Table.Th>
                <Table.Th>Message</Table.Th>
                {unifiedColumns.map((col) => (
                  <Table.Th key={col.key} ta="right">
                    {col.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {steps.map((step) => {
                const Icon = step.config?.Icon;
                const displayName = step.node?.data?.custom?.labels?.id || step.messageName || step.messageId;
                const messageHref = step.config?.urlSlug
                  ? `#/accounts/${accountId}/domains/${domainId}/${step.config.urlSlug}/${step.messageId}/analytics`
                  : null;
                const stepCell = (
                  <Tooltip label={`Step id: ${step.flowStepId}`}>
                    <Text size="sm">#{step.index}</Text>
                  </Tooltip>
                );
                const channelCell = (
                  <Group gap="xs" wrap="nowrap">
                    {Icon && <Icon size={18} />}
                    <Text size="sm">{step.config?.label || step.channel}</Text>
                  </Group>
                );
                const nameCell = messageHref ? (
                  <Anchor href={messageHref} size="sm">
                    {displayName}
                  </Anchor>
                ) : (
                  <Text size="sm">{displayName}</Text>
                );

                if (step.error) {
                  return (
                    <Table.Tr key={step.index}>
                      <Table.Td>{stepCell}</Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>{channelCell}</Table.Td>
                      <Table.Td>{nameCell}</Table.Td>
                      <Table.Td colSpan={Math.max(unifiedColumns.length, 1)}>
                        <Text c="red" size="sm">
                          Failed to load: {step.error || 'Unknown error'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                }
                if (step.unsupported) {
                  return (
                    <Table.Tr key={step.index}>
                      <Table.Td>{stepCell}</Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>{channelCell}</Table.Td>
                      <Table.Td>{nameCell}</Table.Td>
                      <Table.Td colSpan={Math.max(unifiedColumns.length, 1)}>
                        <Text c="dimmed" size="sm">
                          Ad channels are attributed via UTM parameters and can’t be split per step
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                }
                const lookup = columnLookupByScope.get(step.channel);
                return (
                  <Table.Tr key={step.index}>
                    <Table.Td>{stepCell}</Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>{channelCell}</Table.Td>
                    <Table.Td>{nameCell}</Table.Td>
                    {unifiedColumns.map((col) => {
                      const channelCol = lookup?.get(col.key);
                      if (!channelCol) {
                        return <Table.Td key={col.key} ta="right" />;
                      }
                      const cell = step.statsByAction?.[channelCol.stat];
                      return (
                        <Table.Td key={col.key} ta="right">
                          {formatStatValue(channelCol, cell, currency)}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Box>
    </Card>
  );
};

export default StepBreakdown;
